import React, { useEffect, useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { MainLayout, AuthLayout, PublicPageLayout } from './Layout';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import PasswordSet from './pages/PasswordSet';
import RegisterProfile from './pages/RegisterProfile';
import Home from './pages/Home';
import Dashboard from './pages/dashboard/Dashboard';
import { SystemStatusProvider, useSystemStatus } from './contexts/SystemStatusContext';
import StatusBanner from './components/lp/StatusBanner';
import History from './History';
import Customer from './Customer';
import DeleteCustomer from './DeleteCustomer';
import Settings from './Settings';
import CustomerInfo from './CustomerInfo';
import Subscription from './Subscription';
import DeleteScheduled from './DeleteScheduled';
import TeamManagement from './TeamManagement';
import ManagePermissions from './pages/ManagePermissions';
import PlanGuard from './components/PlanGuard';
import PermissionDenied from './pages/PermissionDenied';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminRoute from './AdminRoute';
import Logout from './pages/admin/Logout';
import FaqManagement from './pages/admin/FaqManagement';
import LpManagement from './pages/admin/LpManagement';
import AnnouncementManagement from './pages/admin/AnnouncementManagement';
import StatusManagement from './pages/admin/StatusManagement';
import CsvHistory from './pages/admin/CsvHistory';
import CsvJobDetail from './pages/admin/CsvJobDetail';
import StripeLogs from './pages/admin/StripeLogs';
import OperationLogs from './pages/admin/OperationLogs';
import AuditLog from './pages/admin/AuditLog';
import FailureHistory from './pages/admin/FailureHistory';
import CompanyList from './pages/admin/CompanyList';
import CompanyDetail from './pages/admin/CompanyDetail';
import ActivityLog from './pages/admin/ActivityLog'; 
import UserManagement from './pages/admin/UserManagement'; 
import UserDetail from './pages/admin/UserDetail'; 
import InvoiceManagement from './pages/admin/InvoiceManagement'; 
import AdminBillingList from './pages/admin/AdminBillingList'; 
import AdminBillingDetail from './pages/admin/AdminBillingDetail'; 
import EmailMagazine from './pages/admin/EmailMagazine';
import ReleaseNoteManagement from './pages/admin/ReleaseNoteManagement';
import SystemUserManagement from './pages/admin/SystemUserManagement';
import PermissionGuard from './components/PermissionGuard'; 
import IpRestrictionGuard from './components/IpRestrictionGuard';
import { UserProvider, useUser } from './hooks/UserContext'; 
import { SubscriptionProvider, useSubscription } from './SubscriptionContext';
import { PermissionsProvider } from './PermissionsContext';
import { CookieConsentProvider, useCookieConsent } from './hooks/useCookieConsent';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';
import CookiePolicy from './pages/CookiePolicy';
import { auth } from './firebase';
import { apiClient } from './apiClient';
import ScrollToTop from './components/common/ScrollToTop';
import AnalyticsTracker from './components/AnalyticsTracker';
import ActivityPage from './pages/Activity';
import { UnpaidStatusBanner, SuspendedOverlay } from './components/UnpaidStatusOverlay';
import VerifyEmailChange from './VerifyEmailChange';
import { handleLogout } from './utils/logout';

// --- Public pages ---
import PublicHowToUse from './pages/public/HowToUse';
import PublicHowToUseInvoiceUpload from './pages/public/HowToUseInvoiceUpload';
import PublicHowToUseCustomerUpload from './pages/public/HowToUseCustomerUpload';
import PublicHowToUseCustomerDelete from './pages/public/HowToUseCustomerDelete';
import PublicHowToUseSubscription from './pages/public/HowToUseSubscription.js';
import PublicHowToUseBillwaveSettings from './pages/public/HowToUseBillwaveSettings.js';
import PublicHowToUseCustomerInfo from './pages/public/HowToUseCustomerInfo.js';
import PublicHowToUseTeamManagement from './pages/public/HowToUseTeamManagement.js';
import PublicCsvFormat from './pages/public/CsvFormat';
import PublicContact from './pages/public/Contact';
import PublicFaq from './pages/public/Faq';
import AnnouncementListPage from './pages/public/Announcements';
import Terms from './pages/public/Terms';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import Tokushoho from './pages/public/Tokushoho';

// --- Logged-in support/info pages ---
import ReleaseNotes from './pages/support/ReleaseNotes'; 
import ReleaseNoteDetail from './pages/support/ReleaseNoteDetail'; 
import LoggedInFaqPage from './pages/support/Faq';
import LoggedInHowToUse from './pages/support/HowToUse';
import LoggedInCsvFormat from './pages/support/CsvFormat';
import LoggedInTroubleshooting from './pages/support/Troubleshooting';
import LoggedInContact from './pages/support/Contact';
import InvoiceUploadGuide from './pages/support/HowToUse/InvoiceUpload';
import CustomerUploadGuide from './pages/support/HowToUse/CustomerUpload';
import CustomerDeleteGuide from './pages/support/HowToUse/CustomerDelete';
import SubscriptionGuide from './pages/support/HowToUse/SubscriptionGuide';
import BillwaveSettingsGuide from './pages/support/HowToUse/BillwaveSettings';
import CustomerInfoGuide from './pages/support/HowToUse/CustomerInfoGuide';
import TeamManagementGuide from './pages/support/HowToUse/TeamManagementGuide';
import ActivityGuide from './pages/support/HowToUse/ActivityGuide';
import InvoiceUploadErrors from './pages/support/InvoiceUploadErrors';
import CustomerUploadErrors from './pages/support/CustomerUploadErrors';
import CustomerDeleteErrors from './pages/support/CustomerDeleteErrors';

import InvoiceUpload from './Home';

// --- Billing Pages ---
import BillingHistory from './pages/billing/BillingHistory';
import BillingSnapshot from './pages/billing/BillingSnapshot';

// --- Admin Pages ---
import SystemSettings from './pages/admin/SystemSettings';
import StripeSettings from './pages/admin/StripeSettings';
import MaintenanceSettings from './pages/admin/MaintenanceSettings';
import MaintenancePage from './pages/Maintenance';
import BulkRefundList from './pages/admin/BulkRefundList';
import CreateBulkRefund from './pages/admin/CreateBulkRefund';
import BulkRefundDetail from './pages/admin/BulkRefundDetail';

const InvoiceUploadWithLimitCheck = () => {
    const { plan, invoiceCount } = useSubscription();
    const planLimits = { free: 5, lite: 30, standard: 100, business: 300 };
    const limit = planLimits[plan] || 0;
    const checkLimitBeforeUpload = (file) => {
        return new Promise((resolve) => {
            if (!file) { resolve(true); return; }
            const reader = new FileReader();
            reader.onload = (e) => {
                const lines = e.target.result.split('\n').filter(line => line.trim() !== '');
                if (lines.length <= 1) { resolve(true); return; }
                const header = lines[0].split(',').map(h => h.trim());
                const customerIdIndex = header.indexOf('stripe_customer_id');
                if (customerIdIndex === -1) {
                    alert('CSVファイルに `stripe_customer_id` の列が見つかりません。');
                    resolve(false);
                    return;
                }
                const customerIds = new Set();
                for (let i = 1; i < lines.length; i++) {
                    const columns = lines[i].split(',');
                    if (columns.length > customerIdIndex && columns[customerIdIndex].trim() !== '') {
                        customerIds.add(columns[customerIdIndex].trim());
                    }
                }
                const newInvoiceCount = customerIds.size;
                if (invoiceCount + newInvoiceCount > limit) {
                    alert(`プランの上限を超えます。現在の請求書数: ${invoiceCount}, 新しい請求書数: ${newInvoiceCount}, 上限: ${limit}`);
                    resolve(false);
                } else { resolve(true); }
            };
            reader.onerror = () => { alert('ファイルの読み込み中にエラーが発生しました。'); resolve(false); };
            reader.readAsText(file);
        });
    };
    return <InvoiceUpload onBeforeUpload={checkLimitBeforeUpload} />;
};

const SessionManager = ({ children }) => {
    const navigate = useNavigate();
    const logout = useCallback(async () => {
        await handleLogout();
        sessionStorage.removeItem('loginTimestamp');
        navigate('/login');
    }, [navigate]);

    useEffect(() => {
        let idleTimer;
        const idleTimeout = 30 * 60 * 1000;
        const absoluteTimeout = 24 * 60 * 60 * 1000;
        const resetIdleTimer = () => { clearTimeout(idleTimer); idleTimer = setTimeout(logout, idleTimeout); };
        const checkAbsoluteTimeout = () => {
            const loginTimestamp = sessionStorage.getItem('loginTimestamp');
            if (loginTimestamp && Date.now() - parseInt(loginTimestamp, 10) > absoluteTimeout) { logout(); }
        };
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetIdleTimer));
        resetIdleTimer();
        const absoluteInterval = setInterval(checkAbsoluteTimeout, 60 * 1000);
        return () => {
            clearTimeout(idleTimer);
            clearInterval(absoluteInterval);
            events.forEach(event => window.removeEventListener(event, resetIdleTimer));
        };
    }, [logout]);
    return children;
};

const AuthGuard = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authStatus, setAuthStatus] = useState({ profileComplete: false, isInvited: false });
    const navigate = useNavigate();
    const location = useLocation();
    const { isMaintenanceActive } = useSystemStatus();
    const { isAdmin } = useUser();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            setUser(user);
            if (user) {
                try {
                    const response = await apiClient.get('/v1/user-profile-status');
                    const data = await response.json();
                    setAuthStatus({ profileComplete: data.profileComplete, isInvited: data.isInvited });
                } catch (error) {
                    console.error("Error checking user profile status:", error);
                    await handleLogout();
                    navigate('/login');
                }
            } else {
                setAuthStatus({ profileComplete: false, isInvited: false });
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [navigate]);

    if (loading) {
        return <div style={{ textAlign: 'center', marginTop: '50px' }}>読み込み中...</div>;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Maintenance Mode Redirect
    if (isMaintenanceActive && !isAdmin) {
        return <MaintenancePage />;
    }

    if (!authStatus.profileComplete && location.pathname !== '/register-profile') {
        if (authStatus.isInvited) {
            return <Navigate to="/register-profile?type=invited" replace />;
        } else {
            return <Navigate to="/register-profile" replace />;
        }
    }

    if (authStatus.profileComplete && ['/login', '/signup', '/register-profile', '/set-password'].includes(location.pathname)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
};

const AdminGuard = () => {
    const { isAdmin, loading } = useUser();
    if (loading) { return null; }
    return isAdmin ? <Outlet /> : <Navigate to="/permission-denied" replace />;
};

const CookieConsentContainer = () => {
    const { isModalOpen, closeSettingsModal } = useCookieConsent();
    return (
        <>
            <CookieBanner />
            <CookieSettingsModal isOpen={isModalOpen} onClose={closeSettingsModal} />
        </>
    );
};

function App() {
    const customerFeatures = ['lite', 'standard', 'business'];
    const teamManagementFeatures = ['standard', 'business'];

    return (
        <Router>
            <CookieConsentProvider>
                <AnalyticsTracker />
                <ScrollToTop />
                <CookieConsentContainer />
                <SystemStatusProvider>
                    <UserProvider>
                        <SubscriptionProvider>
                            <PermissionsProvider>
                                <SessionManager>
                                    <StatusBanner />
                                    <UnpaidStatusBanner />
                                    <SuspendedOverlay />
                                    <Routes>
                                        {/* --- Publicly Accessible Routes --- */}
                                        <Route element={<PublicPageLayout />}>
                                            <Route element={<IpRestrictionGuard />}>
                                                <Route path="/" element={<Home />} />
                                            </Route>
                                            <Route path="/announcements" element={<AnnouncementListPage />} />
                                            <Route path="/terms" element={<Terms />} />
                                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                            <Route path="/cookie-policy" element={<CookiePolicy />} />
                                            <Route path="/tokushoho" element={<Tokushoho />} />
                                            <Route path="/support/how-to-use" element={<PublicHowToUse />} />
                                            <Route path="/support/how-to-use/invoice-upload" element={<PublicHowToUseInvoiceUpload />} />
                                            <Route path="/support/how-to-use/customer-upload" element={<PublicHowToUseCustomerUpload />} />
                                            <Route path="/support/how-to-use/customer-delete" element={<PublicHowToUseCustomerDelete />} />
                                            <Route path="/support/how-to-use/subscription" element={<PublicHowToUseSubscription />} />
                                            <Route path="/support/how-to-use/billwave-settings" element={<PublicHowToUseBillwaveSettings />} />
                                            <Route path="/support/how-to-use/customer-info" element={<PublicHowToUseCustomerInfo />} />
                                            <Route path="/support/how-to-use/team-management" element={<PublicHowToUseTeamManagement />} />
                                            <Route path="/support/csv-format" element={<PublicCsvFormat />} />
                                            <Route path="/contact" element={<PublicContact />} />
                                            <Route path="/faq" element={<PublicFaq />} />
                                            <Route path="/verify-email-change" element={<VerifyEmailChange />} />
                                        </Route>

                                        {/* --- Authentication Routes --- */}
                                        <Route element={<AuthLayout />}>
                                            <Route element={<IpRestrictionGuard />}>
                                                <Route path="/login" element={<Login />} />
                                                <Route path="/signup" element={<SignUp />} />
                                            </Route>
                                            <Route path="/set-password" element={<PasswordSet />} />
                                        </Route>

                                        <Route path="/admin/login" element={<AdminLogin />} />

                                        {/* --- Protected Routes (Require Login) --- */}
                                        <Route element={<AuthGuard />}>
                                            <Route element={<AuthLayout />}>
                                                <Route path="/register-profile" element={<RegisterProfile />} />
                                                <Route path="/permission-denied" element={<PermissionDenied />} />
                                            </Route>

                                            <Route element={<MainLayout />}>
                                                
                                                <Route path="/dashboard" element={<Dashboard />} />
                                                <Route path="/invoice-upload" element={<InvoiceUploadWithLimitCheck />} />
                                                
                                                <Route element={<PermissionGuard permission="invoice.canViewHistory" />}>
                                                    <Route path="/billing-history" element={<History />} />
                                                </Route>
                                                <Route element={<PermissionGuard permission="settings.canViewSubscription" />}>
                                                  <Route path="/subscription" element={<Subscription />} />
                                                </Route>

                                                <Route element={<PermissionGuard permission="billing.canViewHistory" />}>
                                                    <Route path="/settings/billing-history" element={<BillingHistory />} />
                                                    <Route path="/settings/billing-history/:snapshotId" element={<BillingSnapshot />} />
                                                </Route>

                                                <Route element={<PermissionGuard permission="invoice.canViewScheduled" />}>
                                                    <Route path="/scheduled-invoices" element={<DeleteScheduled />} />
                                                </Route>
                                                <Route element={<PermissionGuard permission="settings.canViewBillwave" />}>
                                                  <Route path="/settings/billwave" element={<Settings />} />
                                                </Route>
                                                
                                                <Route path="/settings/customer-info" element={<CustomerInfo />} />

                                                <Route element={<PlanGuard allowedPlans={customerFeatures} />}>
                                                    <Route element={<PermissionGuard permission="customer.canUpload" />}>
                                                        <Route path="/customer-upload" element={<Customer />} />
                                                    </Route>
                                                    <Route element={<PermissionGuard permission="customer.canDelete" />}>
                                                        <Route path="/delete-customer" element={<DeleteCustomer />} />
                                                    </Route>
                                                </Route>

                                                <Route element={<PlanGuard allowedPlans={teamManagementFeatures} />}>
                                                    <Route element={<AdminGuard />}>
                                                        <Route path="/settings/team-management" element={<TeamManagement />} />
                                                        <Route path="/settings/team-management/:memberId/permissions" element={<ManagePermissions />} />
                                                        <Route path="/activity" element={<ActivityPage />} />
                                                    </Route>
                                                </Route>

                                                {/* Corrected routes for logged-in support pages */}
                                                <Route path="/app/support/how-to-use" element={<LoggedInHowToUse />} />
                                                <Route path="/app/support/how-to-use/invoice-upload" element={<InvoiceUploadGuide />} />
                                                <Route path="/app/support/how-to-use/customer-upload" element={<CustomerUploadGuide />} />
                                                <Route path="/app/support/how-to-use/customer-delete" element={<CustomerDeleteGuide />} />
                                                <Route path="/app/support/how-to-use/subscription" element={<SubscriptionGuide />} />
                                                <Route path="/app/support/how-to-use/billwave-settings" element={<BillwaveSettingsGuide />} />
                                                <Route path="/app/support/how-to-use/customer-info" element={<CustomerInfoGuide />} />
                                                <Route path="/app/support/how-to-use/team-management" element={<TeamManagementGuide />} />
                                                <Route path="/app/support/how-to-use/activity" element={<ActivityGuide />} />
                                                <Route path="/app/support/csv-format" element={<LoggedInCsvFormat />} />
                                                <Route path="/app/support/faq" element={<LoggedInFaqPage />} />
                                                <Route path="/app/support/troubleshooting" element={<LoggedInTroubleshooting />} />
                                                <Route path="/app/support/troubleshooting/invoice-upload-errors" element={<InvoiceUploadErrors />} />
                                                <Route path="/app/support/troubleshooting/customer-upload-errors" element={<CustomerUploadErrors />} />
                                                <Route path="/app/support/troubleshooting/customer-delete-errors" element={<CustomerDeleteErrors />} />
                                                <Route path="/app/support/contact" element={<LoggedInContact />} />

                                                {/* Release Notes for logged-in users */}
                                                <Route path="/app/release-notes" element={<ReleaseNotes />} />
                                                <Route path="/app/release-notes/:id" element={<ReleaseNoteDetail />} />
                                            </Route>
                                        </Route>

                                        {/* --- Admin Routes --- */}
                                        <Route path="/admin" element={<AdminRoute />}>
                                            <Route element={<AdminLayout />}>
                                                <Route index element={<AdminDashboard />} />
                                                <Route path="companies" element={<CompanyList />} />
                                                <Route path="companies/:tenantId" element={<CompanyDetail />} />
                                                <Route path="users" element={<UserManagement />} />
                                                <Route path="users/:uid" element={<UserDetail />} />
                                                <Route path="invoices" element={<InvoiceManagement />} />
                                                <Route path="csv-history" element={<CsvHistory />} />
                                                <Route path="csv-jobs/:id" element={<CsvJobDetail />} />
                                                <Route path="stripe-logs" element={<StripeLogs />} />
                                                <Route path="activity" element={<ActivityLog />} />

                                                {/* Operator & Admin allowed routes */}
                                                <Route element={<AdminRoute requiredRole="operator" />}>
                                                    <Route path="failure-history" element={<FailureHistory />} />
                                                    <Route path="maintenance-mode" element={<MaintenanceSettings />} />
                                                </Route>

                                                {/* Admin only routes */}
                                                <Route element={<AdminRoute requiredRole="admin" />}>
                                                    <Route path="lp" element={<LpManagement />} />
                                                    <Route path="announcements" element={<AnnouncementManagement />} />
                                                    <Route path="status" element={<StatusManagement />} />
                                                    <Route path="email-magazine" element={<EmailMagazine />} />
                                                    <Route path="billing" element={<AdminBillingList />} />
                                                    <Route path="billing/:invoiceId" element={<AdminBillingDetail />} />
                                                    <Route path="operation-logs" element={<OperationLogs />} />
                                                    <Route path="audit-logs" element={<AuditLog />} />
                                                    <Route path="faq" element={<FaqManagement />} />
                                                    <Route path="release-notes" element={<ReleaseNoteManagement />} />
                                                    <Route path="trial-settings" element={<SystemSettings />} />
                                                    <Route path="stripe-settings" element={<StripeSettings />} />
                                                    <Route path="bulk-refunds" element={<BulkRefundList />} />
                                                    <Route path="bulk-refunds/new" element={<CreateBulkRefund />} />
                                                    <Route path="bulk-refunds/:outageId" element={<BulkRefundDetail />} />
                                                    <Route path="system-users" element={<SystemUserManagement />} />
                                                </Route>
                                                <Route path="logout" element={<Logout />} />
                                            </Route>
                                        </Route>

                                    </Routes>
                                </SessionManager>
                            </PermissionsProvider>
                        </SubscriptionProvider>
                    </UserProvider>
                </SystemStatusProvider>
            </CookieConsentProvider>
        </Router>
    );
}

export default App;
